import React, { useEffect, useMemo, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const getCleanTitle = (movie) => {
  if (!movie?.Title) return movie?.title || 'Untitled';
  return movie.Title.replace(/\s*\(\d{4}\)\s*$/, '');
};

const getYearFromMovie = (movie) => {
  if (!movie) return '';
  if (movie.Year) return String(movie.Year);
  if (movie.year) return String(movie.year);
  if (movie.Released && String(movie.Released).match(/\d{4}/)) {
    return String(movie.Released).match(/\d{4}/)[0];
  }
  if (movie.Title && String(movie.Title).match(/\(\d{4}\)/)) {
    return String(movie.Title).match(/\(\d{4}\)/)[0].replace(/[()]/g, '');
  }
  return '';
};

const bestUrl = (link) =>
  link?.web_url || link?.youtube_url || link?.ios_url || link?.url || '';

const prettyType = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'sub': return 'Subscription';
    case 'free': return 'Free';
    case 'rent': return 'Rent';
    case 'buy': return 'Buy';
    case 'search': return 'Search';
    default: return type || 'Watch';
  }
};

const WatchLinkCard = ({ link, compact = false }) => {
  const url = bestUrl(link);
  if (!url) return null;

  return (
    <a
      className={`watch-link-card ${compact ? 'compact' : ''}`}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className={`watch-link-type ${link.type || 'search'}`}>
        {prettyType(link.type)}
      </span>
      <span className="watch-link-name">{link.label || link.name || 'Open'}</span>
      {link.note && <span className="watch-link-note">{link.note}</span>}
    </a>
  );
};

const WatchOptionsPanel = ({ movie }) => {
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const imdbID = movie?.imdbID;
  const title = useMemo(() => getCleanTitle(movie), [movie]);
  const year = useMemo(() => getYearFromMovie(movie), [movie]);

  useEffect(() => {
    if (!imdbID || imdbID === 'N/A') return;

    const controller = new AbortController();
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ region: 'US' });
        if (title) params.set('title', title);
        if (year) params.set('year', year);
        const response = await fetch(`${API_URL}/watch/${imdbID}?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Watch lookup failed (${response.status})`);
        }
        setOptions(await response.json());
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Watch links are not available right now.');
          setOptions(null);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [imdbID, title, year]);

  if (!imdbID || imdbID === 'N/A') return null;

  const trailer = options?.trailer;
  const sources = options?.sources || [];
  const fallbackLinks = options?.fallback_links || [];
  const hasAnything = trailer || sources.length > 0 || fallbackLinks.length > 0;

  return (
    <section className="watch-options-panel" aria-label={`Watch ${title}`}>
      <div className="watch-options-header">
        <div>
          <h3>Watch now</h3>
          <p>Legal trailer, streaming, rental, and purchase links.</p>
        </div>
        <span className="watch-region-chip">{options?.region || 'US'}</span>
      </div>

      {isLoading && !hasAnything && (
        <div className="watch-loading">Finding legal watch links...</div>
      )}

      {error && !hasAnything && (
        <div className="watch-empty">{error}</div>
      )}

      {options && !isLoading && !error && !hasAnything && (
        <div className="watch-empty">No legal watch links found yet.</div>
      )}

      {trailer?.youtube_key && (
        <div className="watch-video-block">
          <iframe
            title={`${title} trailer`}
            src={`https://www.youtube.com/embed/${trailer.youtube_key}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          <a
            className="watch-youtube-button"
            href={trailer.youtube_url || `https://www.youtube.com/watch?v=${trailer.youtube_key}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Play on YouTube
          </a>
        </div>
      )}

      {sources.length > 0 && (
        <div className="watch-link-section">
          <h4>Available now</h4>
          <div className="watch-link-grid">
            {sources.map((source) => (
              <WatchLinkCard key={`${source.name}-${source.type}`} link={source} />
            ))}
          </div>
        </div>
      )}

      {fallbackLinks.length > 0 && (
        <div className="watch-link-section">
          <h4>Search legal stores</h4>
          <div className="watch-link-grid compact">
            {fallbackLinks.map((link) => (
              <WatchLinkCard key={`${link.name}-${link.type}-${link.web_url}`} link={link} compact />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default WatchOptionsPanel;
